import { Box, Modal } from "@mui/material";
import { cloneDeep, set } from "lodash";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import CtaComponent from "../../../components/reusable-ui/cta";
import { CTA_SIZE, CTA_TYPE } from "../../../data/constants";
import { OUTLET_BULK_ACTIONS } from "../../../data/outlet-status-constants";
import { isMobileDevice } from "../../../helpers/device-info-helper";
import {
  outletBulkMenuSync,
  outletStatusBulkUpdate,
} from "../../../redux/slices";
import OutletToggleForm from "../outlet-toggle-form";
import "./style.scss";

interface Props {
  bulkActionConfig: any;
  setBulkActionConfig: any;
  setApiStateModalData: any;
  setUpdatedOutletsConfig: any;
  flatBranchOutletsGroup: any;
}
const BulkActionConfirmationModal = (props: Props) => {
  const {
    bulkActionConfig,
    setBulkActionConfig,
    setApiStateModalData,
    setUpdatedOutletsConfig,
    flatBranchOutletsGroup
  } = props;
  const [data, setData] = useState<any>({
    active: 1,
    reasonNote: "",
    reason: "",
  });
  const [selectedOutletsList, setSelectedOutletsList] = useState([]);
  const [isChangesMade, setIsChangesMade] = useState<boolean>(false);
  const [errorMsgMap, setErrMsgMap] = useState<any>({});
  const [outletGroupByChannelBranch, setOutletGroupByChannelBranch] =
    useState<any>([]);
  const { businessBranchesDataMap, allChannelsDefDataMap } = useSelector(
    (state: any) => ({
      businessBranchesDataMap: state.businessReducer.businessBranchesDataMap,
      allChannelsDefDataMap: state.businessReducer.allChannelsDefDataMap,
    })
  );
  const dispatch = useDispatch();

  useEffect(() => {
    setSelectedOutletsList(getSelectedOutletList());
  }, [bulkActionConfig?.selectedOutletsMap]);

  useEffect(() => {
    if (selectedOutletsList?.length)
      setOutletGroupByChannelBranch(groupOutletBranchByChannels());
  }, [selectedOutletsList]);

  const onModalClose = () => {
    setBulkActionConfig((prevVal: any) => ({
      ...prevVal,
      showConfirmationModal: false,
    }));
  };

  const handleInputChange = (value: any, fieldToUpdate: string) => {
    let dataToUpdate = cloneDeep(data);
    set(dataToUpdate, fieldToUpdate, value);
    setData(dataToUpdate);
    setIsChangesMade(true);
  };

  const onConfirmationCtaClick = () => {
    if (
      bulkActionConfig?.type ===
      OUTLET_BULK_ACTIONS.bulk_outlet_status_update.type
    ) {
      initiateBulkOutletStatusUpdate();
    } else {
      initiateBulkMenuSync();
    }
  };

  function getSelectedOutletList() {
    let selectedOutletsBranchMap = cloneDeep(
      bulkActionConfig?.selectedOutletsMap || {}
    );
    if (!Object.keys(selectedOutletsBranchMap)?.length) return;
    let resultList: any = flatBranchOutletsGroup(selectedOutletsBranchMap);
    let dataToReturn = resultList.map((outletObj: any) => {
      let newValue = {
        branchId: outletObj?.brid,
        channelDefId: outletObj?.channelDefId,
        outletId: outletObj?.id,
        type: outletObj?.type,
      };
      return newValue;
    });
    return dataToReturn;
  }

  function groupOutletBranchByChannels() {
    let branchGroupByChannelAndAgg: any = {};
    let outletObj: any;
    for (outletObj of selectedOutletsList) {
      let customKey = `${outletObj?.channelDefId}_${outletObj?.outletId}`;
      if (branchGroupByChannelAndAgg[customKey]) {
        branchGroupByChannelAndAgg[customKey]?.branchIds.push(
          outletObj?.branchId
        );
      } else {
        branchGroupByChannelAndAgg[customKey] = {
          branchIds: [outletObj?.branchId],
          channelDefId: outletObj?.channelDefId,
          outletId: outletObj?.outletId,
          type: outletObj?.type,
        };
      }
    }
    let dataToUpdate: any = {};
    let groupObj: any;
    for (groupObj of Object.values(branchGroupByChannelAndAgg)) {
      let branchIdList = groupObj?.branchIds || [];
      branchIdList.sort();
      let key = branchIdList.reduce(
        (ac: string, branchId: string) => `${ac}${ac ? "_" : ""}${branchId}`,
        ""
      );
      if (dataToUpdate[key]) {
        dataToUpdate[key]?.outletList.push({
          channelDefId: groupObj?.channelDefId,
          outletId: groupObj?.outletId,
          type: groupObj?.type,
        });
      } else {
        dataToUpdate[key] = {
          branchIds: groupObj?.branchIds,
          outletList: [
            {
              channelDefId: groupObj?.channelDefId,
              outletId: groupObj?.outletId,
              type: groupObj?.type,
            },
          ],
        };
      }
    }
    return Object.values(dataToUpdate);
  }

  function initiateBulkOutletStatusUpdate() {
    if (!data?.reason && !data?.active) {
      setErrMsgMap({ reason: "Reason is required to turn off." });
      return;
    }
    setErrMsgMap({});
    let dataToPost = {
      data: {
        active: data?.active,
        reason: `${data?.reason}${
          data?.reasonNote ? ` - ${data?.reasonNote}` : ""
        }`,
      },
      outlets: selectedOutletsList,
    };
    const callback = (success: boolean) => {
      if (success) {
        setBulkActionConfig({ active: false });
        setUpdatedOutletsConfig({
          showPill: true,
          updatedOutlets: bulkActionConfig?.selectedOutletsMap,
        });
        setApiStateModalData({
          show: true,
          modalText: "Outlet Status Updated Successfully.",
        });
      }
    };
    dispatch(outletStatusBulkUpdate({ dataToPost, callback }));
  }

  function initiateBulkMenuSync() {
    let dataToPost = {
      outlets: selectedOutletsList,
    };
    const callback = (success: boolean) => {
      if (success) {
        setBulkActionConfig({ active: false });
        setUpdatedOutletsConfig({
          showPill: true,
          updatedOutlets: bulkActionConfig?.selectedOutletsMap,
        });
        setApiStateModalData({
          show: true,
          modalText: "Menu Sync Successful.",
        });
      }
    };
    dispatch(outletBulkMenuSync({ dataToPost, callback }));
  }

  function renderBranchChannelTable() {
    return (
      <div className="sm:overflow-x-auto">
        <div className="border border-[#F5F5F5]">
          <div className="flex items-center bg-[#FAFAFA] border-b border-[#F5F5F5]">
            <div className="text-14 text-[#4B4B4B] font-semibold w-1/3 px-[16px] py-[8px] sm:px-[10px]">
              Branch
            </div>
            <div className="flex items-center w-2/3">
              <div className="text-14 text-[#4B4B4B] font-semibold w-1/2 px-[16px] py-[8px] border-l border-[#F5F5F5] sm:px-[10px]">
                Channel
              </div>
              <div className="text-14 text-[#4B4B4B] font-semibold w-1/2 px-[16px] py-[8px] border-l border-[#F5F5F5] sm:px-[10px]">
                Aggregator
              </div>
            </div>
          </div>
          {outletGroupByChannelBranch?.length
            ? outletGroupByChannelBranch.map((groupObj: any) => {
                return (
                  <div className="flex items-center border-b-[2px] border-[#F0F0F0] last:border-b-0">
                    <div className="text-16 font-medium w-1/3 p-[16px] sm:px-[10px]">
                      {groupObj?.branchIds?.length
                        ? groupObj.branchIds.map((brid: string) => {
                            return (
                              <p className="text-15 text-[#4B4B4B]">
                                {businessBranchesDataMap[brid]?.branchName},
                              </p>
                            );
                          })
                        : null}
                    </div>
                    <div className="w-2/3 border-l border-[#F5F5F5]">
                      {groupObj?.outletList?.length
                        ? groupObj?.outletList.map((data: any) => {
                            return (
                              <div className="flex items-center w-full border-b border-[#F5F5F5] last:border-b-0">
                                <div className="text-15 text-[#4B4B4B] w-1/2 p-[16px] sm:px-[10px]">
                                  {
                                    allChannelsDefDataMap[data?.channelDefId]
                                      ?.name
                                  }
                                </div>
                                <div className="text-15 text-[#4B4B4B] w-1/2 p-[16px] border-l border-[#F5F5F5] capitalize sm:px-[10px]">
                                  {data?.type}
                                </div>
                              </div>
                            );
                          })
                        : null}
                    </div>
                  </div>
                );
              })
            : null}
        </div>
      </div>
    );
  }

  return (
    <Modal open={true} onClose={onModalClose}>
      <Box
        className={`bulk-action-modal absolute w-[636px] h-[668px] rounded-[8px]
          overflow-hidden left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 bg-white
          sm:left-0 sm:right-0 sm:bottom-0 sm:top-auto sm:translate-x-0 sm:translate-y-0
          sm:w-full sm:h-[90%] sm:rounded-b-[0]
        `}
      >
        <div className="flex items-center justify-between py-[12px] px-[24px] sm:px-[16px]">
          <p className="text-22 font-semibold">
            Review - {OUTLET_BULK_ACTIONS[bulkActionConfig?.type].label}
          </p>
          <div
            className="rista-sprite ic-cross-black"
            onClick={onModalClose}
          ></div>
        </div>
        <div className="max-h-[534px] pb-[16px] overflow-y-auto px-[24px] sm:px-[16px]">
          {bulkActionConfig?.type ===
          OUTLET_BULK_ACTIONS.bulk_outlet_status_update.type ? (
            <OutletToggleForm
              data={data}
              handleInputChange={handleInputChange}
              errorMsgMap={errorMsgMap}
              isChangesMade={isChangesMade}
            />
          ) : null}
          {renderBranchChannelTable()}
        </div>
        <div
          className={`absolute bottom-[0px] w-full z-1 right-0 bg-white h-[70px]
            px-24 flex justify-end border-t border-[#EBEBEB] items-center overflow-hidden
            sm:w-full sm:px-[16px]
          `}
        >
          <CtaComponent
            ctaText={"Discard"}
            ctaType={CTA_TYPE.secondary_cta}
            ctaSize={isMobileDevice() ? CTA_SIZE.small : CTA_SIZE.medium}
            onCtaClick={onModalClose}
            customClass={"mr-10 sm:mr-[8px]"}
          ></CtaComponent>
          <CtaComponent
            ctaText={
              OUTLET_BULK_ACTIONS[bulkActionConfig?.type]?.confirmationCtaText
            }
            ctaType={CTA_TYPE.active_cta}
            ctaSize={isMobileDevice() ? CTA_SIZE.small : CTA_SIZE.medium}
            onCtaClick={onConfirmationCtaClick}
            customClass={"ml-10 sm:ml-[8px]"}
          ></CtaComponent>
        </div>
      </Box>
    </Modal>
  );
};

export default BulkActionConfirmationModal;
